/**
 * The Training page's Sessions tab (TRA-1327), staff only: the team's
 * training agent sessions, one of them open beside the list, and the
 * "New session" dialog. Sessions come from the session API (TRA-1322) and
 * the open one follows its event stream.
 */

import { Bot, Plus } from 'lucide-react'
import { useState } from 'react'

import { getApiErrorDetail } from '@/api/client'
import type { TrainingAgentSessionView } from '@/api/generated/model'
import { LoadingPanel } from '@/components/shared/LoadingScreen'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { useAuth } from '@/contexts/AuthContext'
import { useAgentSession, useAgentSessions } from '@/hooks/useTrainingAgent'
import { cn, formatRelativeTime } from '@/lib/utils'

import { STATUS_BADGE, formatUsd } from './display'
import { NewSessionDialog } from './NewSessionDialog'
import { SessionView } from './SessionView'
import { isLive } from './transcript'

function SessionList({
  sessions,
  total,
  selectedId,
  onSelect,
}: {
  sessions: TrainingAgentSessionView[]
  total: number
  selectedId: string | null
  onSelect: (sessionId: string) => void
}): React.JSX.Element {
  return (
    <nav aria-label="Sessions" className="min-h-0 overflow-y-auto border-b border-border lg:border-b-0 lg:border-r">
      <ul className="divide-y divide-border">
        {sessions.map((session) => {
          const badge = STATUS_BADGE[session.status]
          const current = session.id === selectedId
          return (
            <li key={session.id}>
              <button
                type="button"
                aria-current={current ? 'true' : undefined}
                className={cn('w-full space-y-1.5 p-3 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring', current && 'bg-muted')}
                onClick={() => {
                  onSelect(session.id)
                }}
              >
                <span className="block truncate text-sm text-foreground">{session.title}</span>
                <span className="flex flex-wrap items-center gap-2">
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    {formatRelativeTime(session.created_at)} · {formatUsd(session.cost_usd)}
                  </span>
                </span>
              </button>
            </li>
          )
        })}
      </ul>
      {total > sessions.length && (
        <p className="p-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          Newest {sessions.length} of {total}
        </p>
      )}
    </nav>
  )
}

function NewSessionButton({ blocked, onClick }: { blocked: boolean; onClick: () => void }): React.JSX.Element {
  return (
    <div className="ml-auto flex flex-wrap items-center gap-3">
      {blocked && (
        <span className="text-sm text-muted-foreground">Stop the live session to open a new one.</span>
      )}
      <Button disabled={blocked} onClick={onClick}>
        <Plus />
        New session
      </Button>
    </div>
  )
}

interface SessionsTabProps {
  /** The session named in the URL; the newest one opens when it is absent. */
  selectedId: string | null
  onSelect: (sessionId: string) => void
  /** Impersonation reads sessions but cannot open, message or stop one. */
  readOnly: boolean
}

interface SelectedSession {
  session: TrainingAgentSessionView | undefined
  error: string | null
}

/**
 * The session the URL names: from the list's page when it is there, else
 * fetched by id (a link to an older session, or one just created). The
 * newest opens when the URL names none.
 */
function useSelectedSession(
  selectedId: string | null,
  listed: TrainingAgentSessionView[] | undefined,
): SelectedSession {
  const inPage = listed?.find((session) => session.id === selectedId)
  const byId = useAgentSession(
    selectedId ?? '',
    selectedId !== null && listed !== undefined && inPage === undefined,
  )
  if (selectedId === null) return { session: listed?.[0], error: null }
  if (inPage !== undefined) return { session: inPage, error: null }
  return {
    session: byId.data,
    error: byId.isError ? (getApiErrorDetail(byId.error) ?? 'The request failed.') : null,
  }
}

function SelectedPane({
  selected,
  readOnly,
  you,
}: {
  selected: SelectedSession
  readOnly: boolean
  you: string | null
}): React.JSX.Element {
  if (selected.session !== undefined) {
    return (
      <SessionView key={selected.session.id} session={selected.session} readOnly={readOnly} you={you} />
    )
  }
  if (selected.error !== null) {
    return (
      <p role="alert" className="p-4 text-sm text-destructive">
        The linked session did not load: {selected.error}
      </p>
    )
  }
  return <LoadingPanel />
}

function SessionsHeader({
  readOnly,
  blocked,
  onNew,
}: {
  readOnly: boolean
  blocked: boolean
  onNew: () => void
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-start gap-3">
      <div className="space-y-1">
        <h1 className="font-serif text-2xl font-medium text-foreground">Agent sessions</h1>
        <p className="text-sm text-muted-foreground">
          Open a session with the training agent to plan a run from this team&apos;s data.
        </p>
      </div>
      {!readOnly && <NewSessionButton blocked={blocked} onClick={onNew} />}
    </div>
  )
}

export function SessionsTab({ selectedId, onSelect, readOnly }: SessionsTabProps): React.JSX.Element {
  const { email } = useAuth()
  const query = useAgentSessions(true)
  const [creating, setCreating] = useState(false)
  const sessions = query.data?.sessions ?? []
  const selected = useSelectedSession(selectedId, query.data?.sessions)
  const empty = query.data !== undefined && sessions.length === 0 && selectedId === null
  return (
    <div className="space-y-6">
      <SessionsHeader
        readOnly={readOnly}
        blocked={sessions.some((session) => isLive(session.status))}
        onNew={() => {
          setCreating(true)
        }}
      />
      {query.isLoading && <LoadingPanel />}
      {query.isError && (
        <p role="alert" className="text-sm text-destructive">
          The sessions did not load: {getApiErrorDetail(query.error) ?? 'unknown error'}
        </p>
      )}
      {empty && (
        <EmptyState
          icon={Bot}
          title="No sessions yet"
          description="A session starts the training agent in a sandbox with read access to this team's data."
        />
      )}
      {query.data !== undefined && !empty && (
        <div className="grid border border-border lg:h-[calc(100vh-15rem)] lg:min-h-[32rem] lg:grid-cols-[15rem_minmax(0,1fr)]">
          <SessionList
            sessions={sessions}
            total={query.data.total}
            selectedId={selected.session?.id ?? null}
            onSelect={onSelect}
          />
          <SelectedPane selected={selected} readOnly={readOnly} you={email} />
        </div>
      )}
      {!readOnly && <NewSessionDialog open={creating} onOpenChange={setCreating} onCreated={onSelect} />}
    </div>
  )
}
